import { useEffect, useRef, useState } from "react";

/**
 * Holds a boolean true for at least `ms` once it becomes true.
 *
 * Without this, a 40ms cache hit paints a skeleton and tears it down again inside three frames —
 * a flicker that reads as a glitch rather than as loading. Holding briefly makes a fast load look
 * deliberate instead of broken.
 *
 * The cost is honest: a load that would have finished in 40ms now shows content at 300ms. That is
 * the trade this function exists to make, and it is only worth it where a skeleton is actually
 * rendered — never wrap a spinner-free instant transition in it.
 *
 * The alternative — delaying the skeleton by ~150ms so fast loads show nothing at all — avoids
 * both the flicker and the added latency, but shows blank space in the gap. That conflicts with
 * "skeleton appears immediately, no flash of blank space", so this implementation shows the
 * skeleton at once and holds the floor instead.
 */
export function useMinimumDuration(active: boolean, ms = 300): boolean {
  const [held, setHeld] = useState(active);
  const since = useRef<number | null>(active ? Date.now() : null);

  useEffect(() => {
    if (active) {
      // Record the start only on the transition into loading, not on every re-render while
      // loading — otherwise the floor would keep resetting and never elapse.
      if (since.current === null) since.current = Date.now();
      setHeld(true);
      return;
    }

    if (since.current === null) {
      setHeld(false);
      return;
    }

    const elapsed = Date.now() - since.current;
    const remaining = ms - elapsed;
    since.current = null;

    if (remaining <= 0) {
      setHeld(false);
      return;
    }

    const id = setTimeout(() => setHeld(false), remaining);
    return () => clearTimeout(id);
  }, [active, ms]);

  return held;
}
